import { fromEventPattern, Subject, from, AsyncSubject, EMPTY } from 'rxjs';
import { concatMap, distinct, filter, groupBy, map, mergeMap, scan, share, take, toArray, } from 'rxjs/operators';
import { getConnection, } from '@sas-dvr/nova-crossorigin-auth';
import { fetchFontDefinitions } from './FontService';
/**
 * Returns a Subject of key-value pairs where the key is a font family name,
 * and the value is the number of times a font file for that family has been loaded.
 */
export function loadFonts(serverUrl, authenticationType) {
    var _a, _b;
    // Testing only: don't load fonts.
    if ((_b = (_a = window.sas) === null || _a === void 0 ? void 0 : _a.ltjs) === null || _b === void 0 ? void 0 : _b.jest) {
        return loadedFonts$;
    }
    servers$.next([serverUrl, authenticationType]);
    return loadedFonts$;
}
/**
 * Emits the url and authentication type for servers that need fonts loaded.
 * The same values may be emitted multiple times.
 */
const servers$ = new Subject();
/**
 * Emits custom FontFaces as they are loaded.
 */
const fontLoaded$ = new Subject();
const loadedFonts$ = fontLoaded$.pipe(scan((loadedFonts, font) => {
    var _a;
    return Object.assign(Object.assign({}, loadedFonts), { [font.family]: ((_a = loadedFonts[font.family]) !== null && _a !== void 0 ? _a : 0) + 1 });
}, {}), share());
servers$
    .pipe(
// For each unique server, fetch the available font definitions
distinct(([serverUrl, _authType]) => serverUrl), mergeMap(([serverUrl, authType]) => {
    const connection = getConnection(serverUrl, authType);
    return fromEventPattern((handler) => connection.subscribe(handler), (_handler, unsubscribe) => unsubscribe()).pipe(concatMap((status) => {
        if (status.status !== 'authorized') {
            return EMPTY;
        }
        const fonts$ = new AsyncSubject();
        fetchFontDefinitions(serverUrl)
            .then((fonts) => fonts$.next(fonts))
            .catch(() => { }) // supress error and try again when status changes
            .finally(() => fonts$.complete());
        return fonts$;
    }), take(1), 
    // Emit font definitions individually and group by key
    mergeMap((fontDefinitions) => from(fontDefinitions)), groupBy((font) => getFontKey(font)), mergeMap((group$) => group$.pipe(toArray())), 
    // Create a FontFace for each group of definitions
    map((groupedDefinitions) => createFontFace(serverUrl, groupedDefinitions)), filter((fontFace) => fontFace !== null));
}), 
// Avoid adding the same font multiple times
distinct((font) => getFontKey(font)))
    // Add each FontFace to document.fonts and update fontLoaded$ when loaded
    .subscribe((fontFace) => {
    try {
        document.fonts.add(fontFace);
    }
    catch (e) {
        // @font-face declaration may already exist
        return;
    }
    fontFace.loaded
        .then((font) => fontLoaded$.next(font))
        .catch((err) => {
        console.error(`Failed to load font '${fontFace.style} ${fontFace.weight} "${fontFace.family}"' with error: '${err}'`);
    });
});
function getFontKey({ family, weight, style }) {
    return `${family} ${weight} ${style}`;
}
/**
 * Creates a FontFace given a list of FontDefinitions and the server they were obtained from.
 * The first definition is used to set the font metadata.
 */
function createFontFace(serverUrl, fontDefinitions) {
    var _a, _b;
    const fontDefinition = fontDefinitions[0];
    if (!(fontDefinition === null || fontDefinition === void 0 ? void 0 : fontDefinition.family)) {
        return null;
    }
    const opts = { display: 'swap' };
    if (fontDefinition.usWeightClass) {
        opts.weight = '' + fontDefinition.usWeightClass;
    }
    if (fontDefinition.weight) {
        opts.weight = fontDefinition.weight;
    }
    if (fontDefinition.style) {
        opts.style = fontDefinition.style;
    }
    const localSources = [];
    const serverSources = [];
    if (fontDefinition.name) {
        localSources.push(`local('${fontDefinition.name}')`);
    }
    if (fontDefinition.postScriptName && fontDefinition.postScriptName !== fontDefinition.name) {
        localSources.push(`local('${fontDefinition.postScriptName}')`);
    }
    const contentUris = {};
    for (const fd of fontDefinitions) {
        const contentUri = (_b = (_a = fd.links) === null || _a === void 0 ? void 0 : _a.find((link) => link.rel === 'content')) === null || _b === void 0 ? void 0 : _b.uri;
        if (fd.format && contentUri) {
            contentUris[fd.format] = contentUri;
        }
    }
    // Prefer woff over ttf. ttc is not used due to browser compatibility issues.
    if (contentUris.woff) {
        serverSources.push(`url('${serverUrl}${contentUris.woff}') format('woff')`);
    }
    if (contentUris.ttf) {
        serverSources.push(`url('${serverUrl}${contentUris.ttf}') format('truetype')`);
    }
    if (!serverSources.length) {
        return null;
    }
    return new FontFace(fontDefinition.family, [...localSources, ...serverSources].join(', '), opts);
}
